import React, { useCallback, useEffect, useMemo, useState } from "react";
import AddTreatmentOne from "./components/AddTreatmentOne";
import baseUrl from "../../../../api/baseUrl";
import { useDispatch } from "react-redux";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import PercentIcon from "@mui/icons-material/Percent";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LayersIcon from "@mui/icons-material/Layers";
import TimelineIcon from "@mui/icons-material/Timeline";
import InventoryIcon from "@mui/icons-material/Inventory";
import { stageReleasesProducts } from "./stageReleaseFlags";
import { stageLabCategory } from "./stageLabFlags";
import ScienceIcon from "@mui/icons-material/Science";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../redux/loader/loaderActions";

type FlatRow = {
  level: "main" | "type" | "subtype";
  treatment: string;
  type: string;
  subType: string;
  stages: string[];
  releaseStages: string[];
  /** stages that need a lab order: [{ stage, categoryId }] */
  labStages: any[];
  price: number;
  materialCost: number;
  doctorCommission: number;
  doctorCommissionOverrides: any[];
  longTermTreatment: boolean;
  editData: any;
  deactivateData: any;
  label: string;
};

type TreatmentGroup = {
  id: string;
  main: FlatRow;
  children: Array<{ type: FlatRow; subtypes: FlatRow[] }>;
};

const levelStyles = {
  main: {
    badge: "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primaryLight",
    dot: "bg-primary",
    row: "bg-white dark:bg-slate-800",
  },
  type: {
    badge: "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300",
    dot: "bg-indigo-500",
    row: "bg-slate-50/80 dark:bg-slate-800/60",
  },
  subtype: {
    badge: "bg-violet-50 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300",
    dot: "bg-violet-500",
    row: "bg-slate-50/50 dark:bg-slate-800/40",
  },
};

function TreatmentAllList() {
  const [tretmentOpen, settretmentOpen] = useState(false);
  const [data, setData] = useState([]);
  const [dentists, setDentists] = useState<any[]>([]);
  const dispatch = useDispatch();
  const [type, setType] = useState({});
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const getdata = useCallback(() => {
    dispatch(setloadeActive());
    baseUrl
      .get("alltreatment/findAllTreatment")
      .then((response) => {
        dispatch(setloadeDeactive());
        setData(response.data.data);
      })
      .catch((error) => {
        dispatch(setloadeDeactive());
        console.log(error);
      });
  }, [dispatch]);

  useEffect(() => {
    getdata();
  }, [tretmentOpen, getdata]);

  useEffect(() => {
    baseUrl
      .get("appointment/findAllAppointmentDentist")
      .then((res) => setDentists(res.data.data || []))
      .catch(() => setDentists([]));
  }, []);

  const getDentistName = (id: string) => {
    const d = dentists.find((x: any) => String(x._id) === String(id));
    return d ? `${d.title || d.firstName || ""} ${d.lastName || ""}`.trim() || id : id;
  };

  const formatCommissionDisplay = (defaultCommission: number, overrides: any[] = []) => {
    const def = `${defaultCommission ?? 0}%`;
    if (!overrides || overrides.length === 0) return def;
    const parts = overrides.map(
      (o: any) => `${getDentistName(o.dentistId?.toString?.() || o.dentistId)}: ${o.commission ?? 0}%`
    );
    return `${def} (default), ${parts.join(", ")}`;
  };

  const getStages = (stages: any) =>
    Array.isArray(stages) ? stages.map((s) => String(s || "").trim()).filter(Boolean) : [];

  const formatPrice = (treatment: string, price: number) =>
    treatment === "Others" && (price === 0 || price == null) ? "Dynamic" : `Rs ${price}`;

  const buildRow = (
    level: FlatRow["level"],
    label: string,
    treatment: string,
    typeName: string,
    subTypeName: string,
    stages: string[],
    price: number,
    materialCost: number,
    doctorCommission: number,
    doctorCommissionOverrides: any[],
    longTermTreatment: boolean,
    editData: any,
    deactivateData: any
  ): FlatRow => ({
    level,
    treatment,
    type: typeName,
    subType: subTypeName,
    stages,
    price,
    materialCost,
    doctorCommission,
    doctorCommissionOverrides,
    longTermTreatment,
    editData,
    deactivateData,
    label,
    releaseStages: getStages(editData?.releaseStages),
    labStages: Array.isArray(editData?.labStages) ? editData.labStages : [],
  });

  const treatmentGroups = useMemo(() => {
    const groups: TreatmentGroup[] = [];

    data
      ?.filter((item: any) => item.status1 === true)
      ?.forEach((item: any) => {
        const mainRow = buildRow(
          "main",
          item?.treatment,
          item?.treatment,
          "",
          "",
          getStages(item?.stages),
          item?.price,
          item?.materialCost,
          item?.doctorCommission,
          item?.doctorCommissionOverrides || [],
          !!item?.longTermTreatment,
          {
            type: "main",
            treatmentid: item?._id,
            treatment: item?.treatment,
            stages: item?.stages || [],
            releaseStages: item?.releaseStages || [],
            stageCategories: item?.stageCategories || [],
            labStages: item?.labStages || [],
            price: item?.price,
            materialCost: item?.materialCost,
            doctorCommission: item?.doctorCommission,
            doctorCommissionOverrides: item?.doctorCommissionOverrides || [],
            longTermTreatment: !!item?.longTermTreatment,
          },
          { type: "main", mainId: item?._id }
        );

        const children: TreatmentGroup["children"] = [];

        item?.type
          ?.filter((t: any) => t.status2 === true)
          ?.forEach((typeItem: any) => {
            const typeRow = buildRow(
              "type",
              typeItem?.treatment,
              item?.treatment,
              typeItem?.treatment,
              "",
              getStages(typeItem?.stages),
              typeItem?.price,
              typeItem?.materialCost,
              typeItem?.doctorCommission,
              typeItem?.doctorCommissionOverrides || [],
              !!typeItem?.longTermTreatment,
              {
                type: "sub",
                treatmentid: item?._id,
                subId: typeItem?._id,
                treatment: typeItem?.treatment,
                stages: typeItem?.stages || [],
                releaseStages: typeItem?.releaseStages || [],
                stageCategories: typeItem?.stageCategories || [],
                labStages: typeItem?.labStages || [],
                price: typeItem?.price,
                materialCost: typeItem?.materialCost,
                doctorCommission: typeItem?.doctorCommission,
                doctorCommissionOverrides: typeItem?.doctorCommissionOverrides || [],
                longTermTreatment: !!typeItem?.longTermTreatment,
              },
              { type: "sub", mainId: item?._id, subId: typeItem?._id }
            );

            const subtypes: FlatRow[] =
              typeItem?.subType
                ?.filter((s: any) => s.status3 === true)
                ?.map((subtype: any) =>
                  buildRow(
                    "subtype",
                    subtype?.treatment,
                    item?.treatment,
                    typeItem?.treatment,
                    subtype?.treatment,
                    getStages(subtype?.stages),
                    subtype?.price,
                    subtype?.materialCost,
                    subtype?.doctorCommission,
                    subtype?.doctorCommissionOverrides || [],
                    !!subtype?.longTermTreatment,
                    {
                      type: "subsub",
                      treatmentid: item?._id,
                      subId: typeItem?._id,
                      subsubId: subtype?._id,
                      treatment: subtype?.treatment,
                      stages: subtype?.stages || [],
                      releaseStages: subtype?.releaseStages || [],
                      stageCategories: subtype?.stageCategories || [],
                      labStages: subtype?.labStages || [],
                      price: subtype?.price,
                      materialCost: subtype?.materialCost,
                      doctorCommission: subtype?.doctorCommission,
                      doctorCommissionOverrides: subtype?.doctorCommissionOverrides || [],
                      longTermTreatment: !!subtype?.longTermTreatment,
                    },
                    {
                      type: "subsub",
                      mainId: item?._id,
                      subId: typeItem?._id,
                      subsubId: subtype?._id,
                    }
                  )
                ) || [];

            children.push({ type: typeRow, subtypes });
          });

        groups.push({ id: item._id, main: mainRow, children });
      });

    return groups;
  }, [data]);

  const stats = useMemo(() => {
    let typeCount = 0;
    let subtypeCount = 0;
    let stagedCount = 0;

    treatmentGroups.forEach((g) => {
      if (g.main.stages.length > 0) stagedCount += 1;
      g.children.forEach((c) => {
        typeCount += 1;
        if (c.type.stages.length > 0) stagedCount += 1;
        subtypeCount += c.subtypes.length;
        c.subtypes.forEach((s) => {
          if (s.stages.length > 0) stagedCount += 1;
        });
      });
    });

    return {
      main: treatmentGroups.length,
      types: typeCount,
      subtypes: subtypeCount,
      staged: stagedCount,
      total: treatmentGroups.length + typeCount + subtypeCount,
    };
  }, [treatmentGroups]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return treatmentGroups;

    return treatmentGroups
      .map((group) => {
        const mainMatch = [group.main.label, group.main.treatment, ...group.main.stages]
          .join(" ")
          .toLowerCase()
          .includes(q);

        const filteredChildren = group.children
          .map((child) => {
            const typeMatch = [child.type.label, child.type.type, ...child.type.stages]
              .join(" ")
              .toLowerCase()
              .includes(q);
            const filteredSubtypes = child.subtypes.filter((s) =>
              [s.label, s.subType, s.type, ...s.stages].join(" ").toLowerCase().includes(q)
            );
            if (typeMatch || filteredSubtypes.length > 0) {
              return { ...child, subtypes: typeMatch ? child.subtypes : filteredSubtypes };
            }
            return null;
          })
          .filter(Boolean) as TreatmentGroup["children"];

        if (mainMatch || filteredChildren.length > 0) {
          return { ...group, children: mainMatch ? group.children : filteredChildren };
        }
        return null;
      })
      .filter(Boolean) as TreatmentGroup[];
  }, [treatmentGroups, search]);

  useEffect(() => {
    if (search.trim()) {
      const next: Record<string, boolean> = {};
      filteredGroups.forEach((g) => {
        next[g.id] = true;
      });
      setExpanded((prev) => ({ ...prev, ...next }));
    }
  }, [search, filteredGroups]);

  const openModel = () => {
    settretmentOpen(!tretmentOpen);
    setType({});
  };

  const deactivateTreatment = (e: any) => {
    baseUrl
      .post("alltreatment/updateTreatmentStatus", e, { headers: { status: "false" } })
      .then(() => getdata())
      .catch((error) => console.log(error));
  };

  const handleEdit = (e: any) => {
    settretmentOpen(!tretmentOpen);
    setType(e);
  };

  const toggleGroup = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderLongTermBadge = () => (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
      Long Term
    </span>
  );

  const renderLevelBadge = (level: FlatRow["level"]) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${levelStyles[level].badge}`}>
      {level === "main" ? "Treatment" : level === "type" ? "Type" : "Subtype"}
    </span>
  );

  const renderStages = (stages: string[], releaseStages: string[] = [], labStages: any[] = []) => {
    if (stages.length === 0) {
      return <span className="text-xs text-slate-400 dark:text-slate-500 italic">No stages</span>;
    }
    return (
      <div className="flex flex-wrap items-center gap-1">
        {stages.map((stage, index) => (
          <span
            key={`${stage}-${index}`}
            className="inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full text-[11px] font-medium bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/20 text-amber-800 dark:text-amber-200 border border-amber-100 dark:border-amber-800/50"
            title={`Stage ${index + 1}: ${stage}${
              stageReleasesProducts({ releaseStages }, stage) ? " — releases products from inventory" : ""
            }`}
          >
            <span className="w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center shrink-0">
              {index + 1}
            </span>
            {stage}
            {stageReleasesProducts({ releaseStages }, stage) && (
              <InventoryIcon sx={{ fontSize: 12 }} className="text-indigo-500 dark:text-indigo-300" />
            )}
            {stageLabCategory({ labStages }, stage) && (
              <ScienceIcon sx={{ fontSize: 12 }} className="text-emerald-600 dark:text-emerald-300" titleAccess="Needs a lab order" />
            )}
          </span>
        ))}
      </div>
    );
  };

  const renderMetrics = (item: FlatRow) => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold">
        {formatPrice(item.treatment, item.price)}
      </span>
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-medium">
        Cost Rs {item.materialCost || 0}
      </span>
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium max-w-[180px] truncate"
        title={formatCommissionDisplay(item.doctorCommission, item.doctorCommissionOverrides)}
      >
        <PercentIcon sx={{ fontSize: 12 }} />
        {item.doctorCommissionOverrides?.length
          ? formatCommissionDisplay(item.doctorCommission, item.doctorCommissionOverrides)
          : `${item.doctorCommission || 0}%`}
      </span>
    </div>
  );

  const renderActions = (item: FlatRow, compact = false) => (
    <div className={`flex items-center ${compact ? "gap-1" : "gap-1.5"}`}>
      <button
        onClick={() => handleEdit(item.editData)}
        className={`${compact ? "p-1.5" : "px-2.5 py-1.5"} rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-800/50 transition-all hover:scale-105 active:scale-95`}
        title="Edit"
      >
        <BorderColorIcon sx={{ fontSize: compact ? 16 : 17 }} />
      </button>
      {item.treatment !== "Others" && (
        <button
          onClick={() => deactivateTreatment(item.deactivateData)}
          className={`${compact ? "p-1.5" : "px-2.5 py-1.5"} rounded-lg bg-red-50 dark:bg-red-900/40 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800/50 transition-all hover:scale-105 active:scale-95`}
          title="Deactivate"
        >
          <DeleteOutlineIcon sx={{ fontSize: compact ? 16 : 17 }} />
        </button>
      )}
    </div>
  );

  const renderChildRow = (item: FlatRow, depth: number) => (
    <div
      key={`${item.level}-${item.label}-${item.subType || item.type}`}
      className={`relative flex flex-col lg:flex-row lg:items-center gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-700/80 ${levelStyles[item.level].row} transition-colors hover:border-slate-200 dark:hover:border-slate-600`}
      style={{ marginLeft: depth * 16 }}
    >
      <div className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-full ${levelStyles[item.level].dot}`} />
      <div className="flex-1 min-w-0 pl-3">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          {renderLevelBadge(item.level)}
          <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{item.label}</span>
          {item.longTermTreatment && renderLongTermBadge()}
          {item.level === "subtype" && (
            <span className="text-xs text-slate-400 dark:text-slate-500 truncate">
              under {item.type}
            </span>
          )}
        </div>
        <div className="flex items-start gap-1.5 mt-1.5">
          <TimelineIcon sx={{ fontSize: 14 }} className="text-amber-500 mt-0.5 shrink-0" />
          {renderStages(item.stages, item.releaseStages, item.labStages)}
        </div>
      </div>
      <div className="pl-3 lg:pl-0 flex flex-col sm:flex-row sm:items-center gap-2 lg:gap-3">
        {renderMetrics(item)}
        {renderActions(item, true)}
      </div>
    </div>
  );

  const renderEmpty = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-primaryLight/10 flex items-center justify-center mb-4">
        <MedicalServicesIcon sx={{ fontSize: 32 }} className="text-primary" />
      </div>
      <p className="text-base font-semibold text-slate-700 dark:text-slate-200">
        {search ? "No matching treatments" : "No treatments yet"}
      </p>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm">
        {search
          ? "Try a different search term or clear the filter."
          : "Add your first treatment to define pricing, stages, and commissions."}
      </p>
      {!search && (
        <button
          onClick={() => openModel()}
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-gradient-to-r from-primary to-primaryLight text-white rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all font-medium"
        >
          <AddIcon sx={{ fontSize: 18 }} />
          Add Treatment
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-4 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-primary to-primaryLight rounded-xl flex items-center justify-center shadow-lg shadow-primary/20">
              <MedicalServicesIcon className="text-white" sx={{ fontSize: 22 }} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Treatment Catalog</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Manage treatments, types, subtypes & workflow stages
              </p>
            </div>
          </div>
          <button
            onClick={() => openModel()}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm bg-gradient-to-r from-primary to-primaryLight text-white rounded-xl hover:shadow-lg hover:shadow-primary/25 transition-all font-semibold shrink-0"
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Add Treatment
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          {[
            { label: "Treatments", value: stats.main, icon: MedicalServicesIcon, color: "text-primary" },
            { label: "Types", value: stats.types, icon: LayersIcon, color: "text-indigo-500" },
            { label: "Subtypes", value: stats.subtypes, icon: LayersIcon, color: "text-violet-500" },
            { label: "With Stages", value: stats.staged, icon: TimelineIcon, color: "text-amber-500" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600"
            >
              <stat.icon sx={{ fontSize: 18 }} className={stat.color} />
              <div>
                <p className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-none">{stat.value}</p>
                <p className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-0.5">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mt-4">
          <SearchIcon
            sx={{ fontSize: 18 }}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search treatment, type, subtype or stage..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:ring-2 focus:ring-primary/30 focus:border-primary/40 outline-none transition-all"
          />
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {filteredGroups.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
            {renderEmpty()}
          </div>
        ) : (
          filteredGroups.map((group) => {
            const isOpen = expanded[group.id] ?? true;
            const childCount =
              group.children.length + group.children.reduce((n, c) => n + c.subtypes.length, 0);

            return (
              <div
                key={group.id}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden transition-shadow hover:shadow-md"
              >
                {/* Main treatment header */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-700/80 bg-gradient-to-r from-slate-50/80 to-white dark:from-slate-800 dark:to-slate-800">
                  <div className="flex flex-col xl:flex-row xl:items-start gap-3">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      className="flex items-start gap-3 flex-1 min-w-0 text-left group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primaryLight flex items-center justify-center text-white shrink-0 shadow-md shadow-primary/20 group-hover:scale-105 transition-transform">
                        {isOpen ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {renderLevelBadge("main")}
                          <h4 className="text-base font-bold text-slate-800 dark:text-slate-100 truncate">
                            {group.main.label}
                          </h4>
                          {group.main.longTermTreatment && renderLongTermBadge()}
                          {childCount > 0 && (
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-200/70 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                              +{childCount} nested
                            </span>
                          )}
                        </div>
                        <div className="flex items-start gap-1.5 mt-2">
                          <TimelineIcon sx={{ fontSize: 14 }} className="text-amber-500 mt-0.5 shrink-0" />
                          {renderStages(group.main.stages, group.main.releaseStages, group.main.labStages)}
                        </div>
                      </div>
                    </button>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 xl:shrink-0 pl-12 xl:pl-0">
                      {renderMetrics(group.main)}
                      {renderActions(group.main)}
                    </div>
                  </div>
                </div>

                {/* Nested children */}
                {isOpen && group.children.length > 0 && (
                  <div className="p-3 space-y-2 bg-slate-50/50 dark:bg-slate-900/20">
                    {group.children.map((child) => (
                      <div key={child.type.label} className="space-y-2">
                        {renderChildRow(child.type, 0)}
                        {child.subtypes.map((subtype) => renderChildRow(subtype, 1))}
                      </div>
                    ))}
                  </div>
                )}

                {isOpen && group.children.length === 0 && (
                  <div className="px-4 py-3 text-xs text-slate-400 dark:text-slate-500 italic border-t border-slate-100 dark:border-slate-700/80">
                    No types or subtypes under this treatment.
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {tretmentOpen && <AddTreatmentOne type={type} close={settretmentOpen} />}
    </>
  );
}

export default TreatmentAllList;
